<template>
  <div class="certification-category-detail">
    <!-- Breadcrumb -->
    <div class="breadcrumb-container">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/certification' }">职业认证</el-breadcrumb-item>
        <el-breadcrumb-item>认证详情</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="page-content" v-if="category">
      <!-- Header Section -->
      <div class="detail-header">
        <div class="header-content">
          <div class="header-left">
            <img :src="category.image" :alt="category.title" class="category-image">
          </div>
          <div class="header-right">
            <h1 class="category-title">{{ category.title }}</h1>
            <div class="category-meta">
              <span class="meta-item"><i class="el-icon-time"></i> 发布时间：{{ category.createTime || '2023-01-01' }}</span>
              <span class="meta-item"><i class="el-icon-view"></i> 浏览量：{{ category.viewCount || 0 }}</span>
            </div>
            <div class="category-desc">
              {{ category.description || '暂无简介' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Detail Content -->
      <div class="detail-body section-block">
        <div class="section-title">
          <span class="title-text">详细介绍</span>
          <span class="title-line"></span>
        </div>
        <div class="rich-text-content" v-html="category.content || category.description || '暂无详细内容'"></div>
      </div>
    </div>
    
    <div class="page-content" v-else>
      <el-empty description="未找到相关认证类别信息"></el-empty>
    </div>
  </div>
</template>

<script>
import { getCertificateCategories } from '@/api/certificationApi'

export default {
  name: 'CertificationCategoryDetail',
  data() {
    return {
      category: null,
      loading: false
    }
  },
  created() {
    this.initData()
  },
  methods: {
    async initData() {
      // Try to get data from route params first (passed from list page)
      if (this.$route.params.categoryData) {
        this.category = this.$route.params.categoryData
        return
      }

      // If no data in params, fetch by ID (fallback)
      const id = this.$route.params.id
      if (id) {
        await this.fetchCategoryById(id)
      }
    },
    async fetchCategoryById(id) {
      this.loading = true
      try {
        // Since we don't have a specific detail API, we fetch the list and find the item
        // In a real scenario, there should be a detail API like /api/frontend/certificate-category/detail/{id}
        const res = await getCertificateCategories()
        if (res && res.code === 200) {
          const list = res.data || []
          const found = list.find(item => String(item.id) === String(id))
          if (found) {
            this.category = {
              id: found.id,
              title: found.categoryName,
              description: found.introduction ? found.introduction.replace(/<[^>]+>/g, '') : '',
              content: found.introduction, // Assuming introduction contains HTML content
              image: found.coverImage || require('@/assets/new_picture/pic01.png'),
              createTime: found.createTime,
              viewCount: found.viewCount
            }
          }
        }
      } catch (e) {
        console.error('Failed to fetch category detail', e)
        this.$message.error('获取详情失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.certification-category-detail {
  background-color: #f5f7fa;
  min-height: 100vh;
  padding-bottom: 40px;

  .breadcrumb-container {
    width: 1200px;
    margin: 0 auto;
    padding: 20px 0;
  }

  .page-content {
    width: 1200px;
    margin: 0 auto;
  }

  .detail-header {
    background: #fff;
    border-radius: 8px;
    padding: 30px;
    margin-bottom: 20px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);

    .header-content {
      display: flex;
      gap: 30px;

      .header-left {
        flex-shrink: 0;
        width: 400px;
        height: 250px;
        border-radius: 8px;
        overflow: hidden;

        .category-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .header-right {
        flex: 1;
        display: flex;
        flex-direction: column;

        .category-title {
          font-size: 24px;
          color: #333;
          margin: 0 0 15px;
          font-weight: bold;
        }

        .category-meta {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
          color: #999;
          font-size: 14px;

          .meta-item {
            display: flex;
            align-items: center;
            gap: 5px;
          }
        }

        .category-desc {
          font-size: 16px;
          color: #666;
          line-height: 1.6;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
        }
      }
    }
  }

  .section-block {
    background: #fff;
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
    min-height: 400px;

    .section-title {
      display: flex;
      align-items: center;
      margin-bottom: 30px;
      position: relative;
      
      .title-text {
        font-size: 20px;
        font-weight: bold;
        color: #333;
        position: relative;
        z-index: 2;
        padding-right: 15px;
        background: #fff;
      }

      .title-line {
        flex: 1;
        height: 1px;
        background: #eee;
      }

      &::before {
        content: '';
        position: absolute;
        left: -30px;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 20px;
        background: #409EFF;
      }
    }

    .rich-text-content {
      line-height: 1.8;
      color: #333;
      font-size: 16px;
      
      ::v-deep img {
        max-width: 100%;
        height: auto;
      }
      
      ::v-deep p {
        margin-bottom: 15px;
      }
    }
  }
}
</style>